import { Alert, Button } from '@rocket/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { SUBSCRIPTION_UPGRADE_ROUTE } from '../../routes.config';
import { BBDialog } from '../BBDialog/BBDialog';

interface QuotaInfo {
  type?: string;
  used?: number;
  limit?: number;
  resetDate?: string;
}

const WarningIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="#F59E0B" aria-hidden="true">
    <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" />
  </svg>
);

export const QuotaExceededModal = () => {
  const [open, setOpen] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleQuotaExceeded = (e: Event) => {
      const detail = (e as CustomEvent<QuotaInfo>).detail;
      setQuotaInfo(detail);
      setOpen(true);
    };
    window.addEventListener('quotaExceeded', handleQuotaExceeded);
    return () => window.removeEventListener('quotaExceeded', handleQuotaExceeded);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setQuotaInfo(null);
  };

  const handleUpgrade = () => {
    setOpen(false);
    setQuotaInfo(null);
    navigate({ to: SUBSCRIPTION_UPGRADE_ROUTE });
  };

  if (!quotaInfo) {
    return null;
  }

  const label = quotaInfo.type ?? 'documents';

  return (
    <BBDialog
      size="sm"
      isOpen={open}
      onClose={handleClose}
      title="Quota Exceeded"
      actions={
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', width: '100%' }}>
          <Button variant="outline" colorPalette="gray" onClick={handleClose}>
            Not Now
          </Button>
          <Button colorPalette="blue" onClick={handleUpgrade}>
            Upgrade Plan
          </Button>
        </div>
      }
    >
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <WarningIcon />
      </div>
      <h6 style={{ textAlign: 'center', margin: '0 0 12px', fontSize: 18 }}>
        You've reached your {label} limit
      </h6>
      <Alert status="info">
        <div>
          <strong>Current usage:</strong> {quotaInfo.used} / {quotaInfo.limit} {label}
          <br />
          <strong>Resets:</strong>{' '}
          {quotaInfo.resetDate ? new Date(quotaInfo.resetDate).toLocaleDateString() : 'N/A'}
        </div>
      </Alert>
      <p style={{ marginTop: 16 }}>
        Upgrade your plan to continue processing documents or wait until your quota resets.
      </p>
    </BBDialog>
  );
};
